import { createRoot } from "react-dom/client";
import { HelmetProvider } from "react-helmet-async";
// Named imports statt `import * as Sentry` (13.08.2026): Ein Namespace-Import
// zieht das gesamte Paket in den Entry-Graph, weil Rollup nicht sehen kann,
// welche Exporte wirklich benutzt werden. Damit blieb Session Replay im
// kritischen Pfad, egal was man sonst versuchte. Erst mit named imports greift
// Tree-Shaking, und Replay fällt ganz aus dem Bundle — es wird zur Laufzeit
// nachgeladen, siehe loadReplayWhenIdle().
import {
  init as sentryInit,
  captureMessage,
  browserTracingIntegration,
  getClient,
  addIntegration,
  lazyLoadIntegration,
  ErrorBoundary as SentryErrorBoundary,
  setTag as sentrySetTag,
} from "@sentry/react";
import App from "./App.tsx";
import { sentryAppMarkieren, sentryAppRelease } from "@/lib/sentryApp";
import { safeAreaModusSetzen } from "@/lib/platform";
import { captureFbclid } from "@/lib/facebookTracking";
import { captureGclid } from "@/lib/googleTracking";
import { hasStoredMarketingConsent } from "@/hooks/useCookieConsent";
import { formularZustandSichern, formularZustandWiederherstellen } from "@/lib/formularRettung";
import { REPLAY_PFADE, nachLeerlauf, sobaldPfadPasst } from "@/lib/trackingLadezeitpunkt";
import { kurzlinkAnwenden } from "@/lib/kurzlinks";
import { darfMessungSpeichern, nichtErlaubteSpeicherungenEntfernen } from "@/lib/geraeteSpeicher";
import "./index.css";

// Kurzlinks (z. B. charliescrew.com/rudel aus dem Monatsrückblick-Bild) VOR allem anderen auf die
// Zielseite mit UTM umschreiben, damit Sentry, Klick-IDs, useUtmCapture und Sitzungsstart schon die
// Ziel-Adresse sehen. Siehe src/lib/kurzlinks.ts.
kurzlinkAnwenden();

// Fremdcode, dessen Fehler NIE ein Bug von uns sind. Wird sowohl gegen den
// Dateinamen der Stacktrace-Frames geprüft (beforeSend) als auch als denyUrls.
//   - contentsquare: Analytics-Tag, fängt intern ab, Seite läuft weiter
//   - iabjs://      : In-App-Browser von Meta (Facebook/Instagram). Injiziert
//                     eigene Logger in unsere Seite; die Fehler entstehen beim
//                     Abbau der Webview. Skaliert linear mit dem Ad-Spend.
const THIRD_PARTY_SOURCES = [
  "contentsquare.net",
  "t.contentsquare",
  "iabjs://",
  "connect.facebook.net",
  "googletagmanager.com",
];

// Wird gesetzt, sobald wegen eines Stale-Chunks ein Reload läuft (siehe
// vite:preloadError-Handler weiter unten). Alles, was danach noch an Fehlern
// aufschlägt, ist Folgeschaden einer Seite, die sich gerade selbst neu lädt —
// und wird in beforeSend verworfen.
let staleChunkReloadInFlight = false;

sentryInit({
  dsn: "https://a634492e2b9dd3bc676cead74c35fc5a@o4511428534403072.ingest.de.sentry.io/4511428643651664",
  environment: import.meta.env.MODE,

  // Nur Produktion melden. Vorher landeten lokale Dev-Sessions mit im Projekt
  // (z.B. "Failed to fetch dynamically imported module: http://localhost:8080/...")
  // und haben die Issue-Liste verwässert.
  enabled: import.meta.env.PROD,

  // Source Maps: Sentry ordnet Events einem Release zu und findet darüber die
  // hochgeladenen/erreichbaren Maps. Ohne Release sind Stacks minifiziert.
  release: import.meta.env.VITE_SENTRY_RELEASE || undefined,

  ignoreErrors: [
    /window\.webkit\.messageHandlers/,
    /webkit\.messageHandlers/,
    // Meta In-App-Browser (siehe THIRD_PARTY_SOURCES). Die Meldungen kommen in
    // mehreren Varianten, immer nach dem Muster "Error invoking <methode>: ...".
    /Java object is gone/,
    /Java exception was raised during method invocation/,
    /Error invoking (postMessage|enable\w+Logging)/,
  ],

  denyUrls: [/iabjs:\/\//, /contentsquare\.net/, /t\.contentsquare/],

  beforeSend(event) {
    // Wir laden gerade wegen eines Stale-Chunks neu. Was jetzt noch an
    // Fehlern kommt, ist Folgeschaden dieses Reloads und kein eigener Bug.
    if (staleChunkReloadInFlight) return null;

    // Kunden-App: Release = App-Version + Build + Capgo-Bundle (Launch-Check A21).
    const appRelease = sentryAppRelease();
    if (appRelease) event.release = appRelease;

    // Fremdcode anhand der Stacktrace-Frames rausfiltern.
    //
    // ACHTUNG — bewusst eng gehalten: Es wird NUR verworfen, wenn ein Frame
    // eindeutig einer fremden Quelle zuzuordnen ist. Events ohne auflösbaren
    // Dateinamen (filename === undefined, typisch bei cross-origin Skripten)
    // werden NICHT verworfen — dort steckt u.a. der offene
    // "RangeError: Maximum call stack size exceeded" auf /dashboard drin,
    // der eine echte Kundin betrifft.
    const frames = event.exception?.values?.[0]?.stacktrace?.frames ?? [];
    const isThirdParty = frames.some((f) =>
      THIRD_PARTY_SOURCES.some((src) => f.filename?.includes(src))
    );
    return isThirdParty ? null : event;
  },

  // browserTracing bleibt statisch: es misst den Seitenaufbau selbst mit
  // (Navigation-Span ab Dokumentstart). Lädt man es nach, misst es die
  // Ladephase nicht mehr, die wir gerade beobachten wollen — und es ist
  // klein. Replay dagegen wird unten nachgeladen, siehe loadReplayWhenIdle().
  integrations: [
    browserTracingIntegration(),
  ],
  tracesSampleRate: 0.1,
  replaysSessionSampleRate: 0.1,
  replaysOnErrorSampleRate: 1.0,
});

// Plattform (ios | android | web), App-Build und Capgo-Bundle an jedes Event hängen.
sentryAppMarkieren(sentrySetTag);

// iOS-App: oberer Safe-Area-Abstand je nach Hülle (contentInset automatic bis 1.0, never ab 1.0.1).
safeAreaModusSetzen();

// Session Replay nachladen, sobald der Browser nichts Wichtigeres zu tun hat —
// und nur im eingeloggten Bereich (REPLAY_PFADE).
//
// EINGRENZUNG AUF EINGELOGGTE PFADE (18.09.2026, Perf-Audit Punkt 3)
// Auch nach Leerlauf kostete replay.min.js auf anonymen Marketing-Besuchen
// ~150 ms Skriptzeit im Labor, ohne dass dort je jemand ein Replay ansah.
// Deshalb lädt Replay nur noch auf /dashboard*, /admin*, /walker*, /daycare*,
// /dog/* und /export*; auch dort erst nach Leerlauf. Landet jemand über
// /login und navigiert erst danach ins Dashboard, greift `sobaldPfadPasst`
// beim Routenwechsel. TRADE-OFF: Fehler auf Marketing-Seiten haben kein
// Replay mehr, nur Stacktrace und Breadcrumbs. Gewollt: die Seiten sind
// anonym, und der Erstpaint entscheidet dort über die Ads-Kosten.
//
// WARUM ÜBERHAUPT NACHLADEN (13.08.2026, Messung auf /schnuppertag)
// `vendor-sentry` hing mit 94 KB gzip als `modulepreload` im kritischen Pfad
// jeder Seite — rund ein Drittel des JS, das ein Erstbesucher herunterladen
// muss, bevor React überhaupt mountet. Der weitaus größte Teil davon ist
// Replay. Auf /schnuppertag, wo bezahlter Google-Ads-Traffic von Neukunden
// landet, zahlt genau der Besucher diesen Download, den wir gewinnen wollen.
// Feldwerte vorher (p75, 30 Tage, Tabelle `web_vitals`): TTFB 1085 ms,
// FCP 1904 ms, LCP 2010 ms, INP p95 617 ms.
//
// Replay ist das einzige Sentry-Feature, das ohne Verlust warten kann: es
// zeichnet auf, was der Nutzer TUT — und in der Zeit vor `load` tut er nichts,
// weil noch nichts steht. Fehlererfassung, Breadcrumbs und Tracing laufen
// unverändert ab der ersten Zeile weiter, weil sentryInit() oben unangetastet
// bleibt.
//
// TRADE-OFF, bewusst in Kauf genommen
// `replaysOnErrorSampleRate: 1.0` puffert normalerweise durchgehend mit, damit
// bei einem Fehler die Vorgeschichte im Replay steckt. Für Fehler, die VOR
// `load` auftreten, fehlt diese Vorgeschichte jetzt. Der Fehler selbst wird
// weiterhin gemeldet — nur ohne Video davor. Das ist der Preis für ~90 KB gzip
// weniger im kritischen Pfad.
//
// KEIN Marketing-Tracking: Meta-Pixel/CAPI (captureFbclid) und Google Ads
// (captureGclid) bleiben unberührt und laufen weiter wie unten definiert.
function loadReplayWhenIdle() {
  if (!import.meta.env.PROD) return;

  // EINWILLIGUNG (Noah 27.10.2026, Datenstrategie D0b): Replay legt eine Sitzung im
  // sessionStorage ab und zeichnet die Nutzung auf, deshalb nur mit Statistik-Einwilligung
  // und in der App gar nicht (darfMessungSpeichern ist dort immer false). Geprüft wird beim
  // Start, also nach Pfad und Leerlauf: Wer in dieser Sitzung erst später zustimmt, bekommt
  // Replay ab dem nächsten Seitenladen. Fehlermeldungen an Sentry laufen unverändert weiter
  // (ohne Speicherung auf dem Gerät).
  const start = () => {
    if (!darfMessungSpeichern()) return;
    // lazyLoadIntegration() statt eines eigenen dynamischen Imports.
    //
    // Ein `import("@sentry/react")` bringt hier NICHTS: Rollup fasst statischen
    // und dynamischen Import desselben Moduls zu einem Chunk zusammen, der dann
    // wieder statisch geladen wird (gemessen: vendor-sentry blieb bei 95 KB
    // gzip). Erst wenn `replayIntegration` NIRGENDS mehr statisch referenziert
    // wird, greift Tree-Shaking — und genau das erreicht lazyLoadIntegration,
    // weil es den Replay-Code zur Laufzeit vom Sentry-CDN holt.
    // Gemessen: vendor-sentry 95,09 -> 53,46 KB gzip.
    lazyLoadIntegration("replayIntegration")
      .then((replayIntegration) => {
        // Doppeltes Einhängen vermeiden, falls load/idle mehrfach feuern.
        if (getClient()?.getIntegrationByName("Replay")) return;
        addIntegration(replayIntegration());
      })
      .catch(() => {
        // Replay ist Diagnose-Komfort, kein Feature. Schlägt der Nachladeweg
        // fehl (Offline, Adblocker, CDN blockiert), läuft die Seite normal
        // weiter und Sentry meldet Fehler trotzdem — nur ohne Video.
      });
  };

  // Erst der passende Pfad (jetzt oder nach einer SPA-Navigation), dann `load`
  // und Leerlauf. Ein Replay im Nachhinein zu starten ist unkritisch: es
  // zeichnet ab dem Einhängen auf, und der Fehler selbst wird ohnehin gemeldet.
  sobaldPfadPasst(REPLAY_PFADE, () => nachLeerlauf(start, { spaetestensMs: 5000 }));
}

loadReplayWhenIdle();

// One-time cleanup: remove stale Supabase auth cookies that cause
// session collisions across Lovable projects on .lovable.app
(function cleanupSupabaseCookies() {
  try {
    document.cookie.split(';').forEach(cookie => {
      const name = cookie.split('=')[0].trim();
      if (name.startsWith('sb-') || name.includes('supabase')) {
        document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; domain=.lovable.app`;
        document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`;
      }
    });
  } catch {
    // Ignore cookie cleanup errors
  }
})();

// Meta: fbclid der Landing-URL erfassen, consent-gated wie bei Google (02.10.2026,
// Datenschutz-Prüfung): ohne gespeicherte Marketing-Einwilligung nur im Speicher;
// das `_fbc`-Cookie folgt beim Opt-in (useCookieConsent.saveConsent → persistFbc).
captureFbclid(hasStoredMarketingConsent());

// Google Ads: gclid/gbraid/wbraid der Landing-URL erfassen — consent-gated
// (21.07.2026, Voraussetzung Offline-Conversion-Upload): ohne gespeicherten
// Marketing-Consent nur In-Memory-Stash; Cookie folgt beim Banner-Opt-in
// (useCookieConsent.saveConsent → persistGclid).
captureGclid(hasStoredMarketingConsent());

// Datenstrategie D0 (27.10.2026, § 25 TDDDG): Bis heute legten Website-Messung, A/B-Test und
// Kampagnen-Herkunft Cookies/Storage auch ohne Einwilligung an. Diesen Altbestand (und alles, was
// nach einem Widerruf übrig ist) einmal entfernen. Ohne Entscheidung im Banner gilt: keine
// Einwilligung. Erst nach Leerlauf, damit der Start nichts davon merkt.
nachLeerlauf(() => nichtErlaubteSpeicherungenEntfernen());

// CRITICAL: Check for password recovery token BEFORE React mounts
// Supabase will process and clear the hash, so we capture it first
const hash = window.location.hash;
const currentPath = window.location.pathname;
const searchParams = new URLSearchParams(window.location.search);

// Detect recovery from:
// 1. Hash with type=recovery (implicit/classic flow)
// 2. Explicit ?recovery param
// 3. ?code= param on /auth when password_recovery_pending is already set
const hasRecoveryHash = hash && hash.includes("type=recovery");
const hasExplicitRecovery = currentPath.includes("/auth") && searchParams.has("recovery");
const hasPkceCode = searchParams.has("code") && currentPath.includes("/auth");

if (hasRecoveryHash || hasExplicitRecovery || (hasPkceCode && sessionStorage.getItem("password_recovery_pending") === "true")) {
  sessionStorage.setItem("password_recovery_pending", "true");

  // If we're not on /auth, redirect there with the hash
  if (!currentPath.includes("/auth")) {
    window.location.href = `/auth${hash}`;
  }
}

// Stale-Chunk nach Deploy: Auto-Reload, damit Nutzer (v.a. Walker mit offener
// PWA) nach einem Publish nicht in 'Failed to fetch dynamically imported
// module' / 'Importing a module script failed' hängenbleiben.
//
// Die Vorgängerversion (Muster aus charlies-team, Juni 2026) hatte drei Löcher:
//
//  1. Der sessionStorage-Guard wurde EINMAL gesetzt und nie geräumt. Nach dem
//     ersten Stale-Chunk einer Tab-Session heilte kein weiterer mehr — bei
//     einer tagelang offenen PWA also praktisch nie wieder. Genau daher kommt
//     der bekannte Workaround "App schließen und neu öffnen": das Schließen
//     leert die sessionStorage. Jetzt ein Cooldown-Zeitstempel: schützt
//     weiterhin vor Reload-Loops, heilt aber bei jedem künftigen Deploy erneut.
//
//  2. Selbst im geheilten Fall lief der Fehler noch nach Sentry — Rauschen für
//     einen Vorgang, der sich selbst repariert. Gelöst über das Flag
//     `staleChunkReloadInFlight`, das beforeSend auswertet.
//
//     NICHT über event.preventDefault() lösen! Das war der erste Versuch
//     (PR #234) und hat am 03.08.2026 eine Regression verursacht. Grund steht
//     in Vites Preload-Helper:
//
//         return baseModule().catch(handlePreloadError);
//
//     handlePreloadError wirft nur weiter, wenn NICHT preventDefault gerufen
//     wurde. Mit preventDefault schluckt das .catch() den Fehler, der
//     dynamische Import resolved zu `undefined`, und React.lazy stirbt beim
//     Zugriff darauf:
//     "TypeError: undefined is not an object (evaluating 'e._result.default')".
//     Aus einem selbstheilenden Reload wurde so ein harter Absturz.
//
//  3. Wenn der Reload NICHT half, blieb es still. Genau das ist aber das
//     einzige Signal, das uns interessiert — es wird jetzt explizit gemeldet.
const RELOAD_STAMP_KEY = 'vite-preload-reloaded-at';
const RELOAD_COOLDOWN_MS = 15_000;

window.addEventListener('vite:preloadError', (event) => {
  const lastReload = Number(sessionStorage.getItem(RELOAD_STAMP_KEY)) || 0;
  const sinceLastReload = Date.now() - lastReload;

  if (lastReload && sinceLastReload < RELOAD_COOLDOWN_MS) {
    // Wir haben gerade erst neu geladen und der Chunk fehlt immer noch.
    // Kein zweiter Reload (das wäre die Endlosschleife) — stattdessen den
    // Fehler bewusst durchlaufen lassen, damit die ErrorBoundary greift.
    captureMessage('Stale-Chunk-Reload hat nicht geholfen', {
      level: 'error',
      extra: {
        sinceLastReloadMs: sinceLastReload,
        path: window.location.pathname,
        // `payload` hängt Vite an das Event; enthält den Originalfehler.
        originalError: String((event as Event & { payload?: unknown }).payload),
      },
    });
    return;
  }

  sessionStorage.setItem(RELOAD_STAMP_KEY, String(Date.now()));
  // Eingaben retten, bevor wir die Seite unter der Kundin wegziehen.
  // Caroline Reuter, 18.09.2026: „die Website stürzt hier und da ab." Technisch
  // heilt der Reload alles — was sie verliert, sind die getippten Werte. Genau das
  // fühlt sich wie ein Absturz an. Siehe src/lib/formularRettung.ts.
  formularZustandSichern();
  // Bewusst KEIN event.preventDefault() (siehe Punkt 2 oben). Vite muss den
  // Fehler weiterwerfen dürfen, sonst resolved der Import zu undefined.
  // Das Flag hält stattdessen die Folgefehler aus Sentry heraus.
  staleChunkReloadInFlight = true;
  window.location.reload();
});

// Google Translate vs. React.
//
// Translate ersetzt Textknoten durch <font>-Elemente. React verliert dadurch
// die Knoten, die es verwaltet, und wirft beim nächsten Update einen
// NotFoundError in insertBefore/removeChild. Die ErrorBoundary greift, und die
// Kundin sieht "Etwas ist schiefgelaufen" statt ihres Dashboards.
//
// Belegt am 03.08.2026 (Sentry CHARLIESCREW-5E + 5F, beide dieselbe Kundin):
// Browser-Locale en-GB auf unserer deutschsprachigen Seite, und der
// Breadcrumb-Selektor des letzten Klicks endete auf "> font > font" — die
// Signatur von Translate.
//
// Bewusst KEIN <meta name="google" content="notranslate">: das schaltet die
// Übersetzung komplett ab und trifft damit genau die Kundinnen, die sie
// brauchen — englischsprachige Hundehalter in München sind ein realer Anteil.
// Wir reparieren lieber die Verträglichkeit, statt die Funktion zu nehmen.
//
// Stattdessen der etablierte defensive Patch (facebook/react#11538): Ein
// Aufruf mit fremdem Parent wird zum No-op statt zu werfen. React rendert den
// betroffenen Teilbaum beim nächsten Update ohnehin neu.
//
// TRADE-OFF: Der Patch verschluckt auch echte DOM-Fehler. Damit wir dabei
// nicht blind werden, wird der erste abgefangene Fall pro Session an Sentry
// gemeldet — einmal, damit es kein Rauschen wird.
(function patchDomAgainstTranslationExtensions() {
  if (typeof Node !== "function" || !Node.prototype) return;

  let reportedThisSession = false;
  const reportOnce = (method: string) => {
    if (reportedThisSession) return;
    reportedThisSession = true;
    captureMessage(`DOM-Patch: ${method} mit fremdem Parent abgefangen`, {
      level: "warning",
      extra: { method, path: window.location.pathname, language: navigator.language },
    });
  };

  const originalRemoveChild = Node.prototype.removeChild;
  Node.prototype.removeChild = function removeChild(child) {
    if (child.parentNode !== this) {
      reportOnce("removeChild");
      return child;
    }
    return originalRemoveChild.call(this, child);
  };

  const originalInsertBefore = Node.prototype.insertBefore;
  Node.prototype.insertBefore = function insertBefore(newNode, referenceNode) {
    if (referenceNode && referenceNode.parentNode !== this) {
      reportOnce("insertBefore");
      return newNode;
    }
    return originalInsertBefore.call(this, newNode, referenceNode);
  };
})();

// Gerettete Eingaben zurückschreiben, falls dieser Seitenaufruf ein erzwungener
// Neustart nach einem Publish war (siehe vite:preloadError-Handler oben).
formularZustandWiederherstellen();

createRoot(document.getElementById("root")!).render(
  <HelmetProvider>
    <SentryErrorBoundary
      fallback={
        <div style={{ padding: "2rem", textAlign: "center" }}>
          <h1>Etwas ist schiefgelaufen.</h1>
          <p>Bitte lade die Seite neu oder versuche es später erneut.</p>
        </div>
      }
    >
      <App />
    </SentryErrorBoundary>
  </HelmetProvider>
);
